import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { WebSessionUser } from '../types';
import { getWebPath } from '../pathPrefix';
import { useState, useRef, useEffect } from 'react';
import { getFlagIconForLanguage } from '../../miniapp/i18n/flags';
import BrandLogo from '../../miniapp/ui-kit/BrandLogo';
import Icon from '../../miniapp/ui-kit/Icon';

interface HeaderProps {
  cartCount: number;
  sessionUser: WebSessionUser | null;
  logout: () => Promise<void>;
  logoUrl: string | null;
}

function navButtonClass(isActive: boolean): string {
  return `flex items-center justify-center border-0 rounded-full w-10 h-10 cursor-pointer bg-secondary text-text transition-all hover:bg-secondary/80 ${isActive ? 'ring-2 ring-accent/40' : ''}`;
}

export default function Header({ cartCount, sessionUser, logout, logoUrl }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ka', name: 'ქართული' },
    { code: 'ru', name: 'Русский' },
  ];

  const currentLangCode = i18n.resolvedLanguage || i18n.language || 'en';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-10 bg-bg/90 backdrop-blur-md border-b border-border">
      <div className="max-w-web mx-auto min-h-[var(--web-header-height)] px-[var(--web-side-padding)] py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-6">
          <NavLink to="/" className="inline-flex items-center no-underline" aria-label={t('website.header.brandAria')}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="block h-10 w-auto max-w-[160px] object-contain sm:h-12 sm:max-w-[220px]"
              />
            ) : (
              <BrandLogo size="md" />
            )}
          </NavLink>
          <NavLink
            to="/"
            className={({ isActive }) =>
              `no-underline font-medium text-sm px-4 py-2 rounded-full transition-colors hover:bg-secondary ${isActive ? 'bg-secondary' : 'text-text-secondary'}`
            }
            end
          >
            {t('header.titleCatalog')}
          </NavLink>
        </div>

        <nav className="flex gap-2 items-center">
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary text-text text-sm font-medium hover:bg-secondary/80 transition-colors border-0 cursor-pointer"
            >
              <Icon name="language" className="text-[20px]" />
              <span className="uppercase">{currentLangCode.split('-')[0]}</span>
            </button>
            {isLangOpen && (
              <div className="absolute top-full right-0 mt-1 bg-bg border border-border rounded-xl shadow-lg overflow-hidden min-w-[140px] z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      void i18n.changeLanguage(lang.code);
                      setIsLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-secondary transition-colors flex items-center gap-2 border-0 bg-transparent cursor-pointer text-text ${
                      currentLangCode.startsWith(lang.code) ? 'bg-secondary font-semibold' : ''
                    }`}
                  >
                    <span className="text-base leading-none">{getFlagIconForLanguage(lang.code)}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <NavLink to="/cart" className={({ isActive }) => navButtonClass(isActive)} title={t('cart.title')}>
            <div className="relative flex items-center justify-center">
              <Icon name="shopping_cart" />
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 rounded-full bg-accent text-on-accent text-[10px] font-bold flex items-center justify-center px-1">
                  {cartCount}
                </span>
              )}
            </div>
          </NavLink>

          <NavLink to="/orders" className={({ isActive }) => navButtonClass(isActive)} title={t('orders.title')}>
            <Icon name="receipt_long" />
          </NavLink>

          {(sessionUser?.isManager || sessionUser?.isCourier) && (
            <a
              className="border-0 rounded-full px-4 py-2 cursor-pointer bg-secondary text-text no-underline font-medium text-sm hover:bg-secondary/80 transition-colors"
              href={getWebPath('/manager')}
            >
              {t('website.header.manager')}
            </a>
          )}

          {sessionUser ? (
            <>
              <NavLink to="/profile" className={({ isActive }) => navButtonClass(isActive)} title={t('settings.title')}>
                <Icon name="person" />
              </NavLink>
              <button
                type="button"
                className={navButtonClass(false)}
                onClick={() => void logout()}
                title={t('website.header.logout')}
              >
                <Icon name="logout" />
              </button>
            </>
          ) : (
            <NavLink to="/login" className={({ isActive }) => navButtonClass(isActive)} title={t('website.header.login')}>
              <Icon name="login" />
            </NavLink>
          )}
        </nav>
      </div>
    </header>
  );
}
